import { Injectable, Inject } from '@kaokei/use-vue-service';
import { DaoService } from '../dao.service';

import {
  QuestionDto,
  ExamPaperRespDto,
  PaperAnswerRecord,
  UserAnswerRecord,
  UserInfo4Exam,
  UserAnswerDto,
  UserExam,
  MarkRecordRespMap,
  AttachmentAnswerItem,
} from '@/types';
import {
  IS_QUESTION_TYPE_CHOICE,
  IS_QUESTION_TYPE_FILE,
  IS_QUESTION_TYPE_FILL_BLANK,
  IS_QUESTION_TYPE_PROGRAM,
  letterToNumber,
  logger,
} from '@/utils';

import { CheatService } from '@/services/exam/cheat.service';
import { QuestionService } from '@/services/exam/question.service';
import { CountdownService } from '@/services/exam/countdown.service';
import { EnvCheckService } from './envCheck.service';
import { UserService } from '../user.service';
import { TrtcService } from './trtc.service';

// 试卷信息和用户答案和用户信息

@Injectable()
export class ExamService {
  @Inject(CheatService)
  public cheatService!: CheatService;

  @Inject(QuestionService)
  public questionService!: QuestionService;

  @Inject(CountdownService)
  public countdownService!: CountdownService;

  @Inject(EnvCheckService)
  public envCheckService!: EnvCheckService;

  @Inject(UserService)
  public userService!: UserService;

  @Inject(TrtcService)
  public trtcService!: TrtcService;

  /**
   * 赛事活动id
   */
  public contestInfoId = 0;

  /**
   * 查看答案模式下采用当前模式
   * 兼容多级阅卷用
   */
  public reviewAreaId = 0;

  /**
   * 标志已交卷
   * 离开当前页面
   * 未能成功进入考试页面
   *
   * @memberof ExamService
   */
  public destroyed = false;

  /**
   * 本次考试的ID
   * 从url上获取的字段
   *
   * @memberof ExamService
   */
  public userExamId = 0;

  /**
   * 本次考试对应的答卷ID，目前和userExamId是一一对应的，但是未来可能会存在一对多的关系
   * 比如将来支持了模拟赛，模拟赛就需要支持多次答卷
   * 但是需要注意模拟卷不一定使用这个设计架构
   *
   * @memberof ExamService
   */
  public answerPaperId = 0;

  /**
   * 本次考试对应的答卷ID，是否启用配置了先完成客观题，在完成主观题的配置参数
   * 通常默认为0，表示没有启用，还是走以前的逻辑
   * 如果配置为1，后台会自动对题目排序，客观题在前，主观题在最后。
   * 要求是客观题全部做完了，才能去做主观题，开始做了主观题，客观题就无法再查看喝修改
   *
   * @memberof ExamService
   */
  public answerOrderLimit = 0;

  /**
   * 本次考试对应的答卷ID，配置了先完成客观题，再完成主观题的配置参数
   * hasFinishedObjective 标记当前学生的本次考试，是否已经完成客观题，并开始主观题答题。
   * 为0，表示用户还在做客观题，没有开始答主观题目。
   * 为1，表示用户已经开始做主观题，那么客观题降不能再次修改和查看，只能查看并修改主观题。
   * 这里的状态判断和变更，是根据学生确定开始做其中一个主观题时，接口上报給服务端的
   *
   * @memberof ExamService
   */
  public hasFinishedObjective = 0;

  /**
   * 考试模式 还是 查看答案模式
   * 默认是考试模式
   *
   * @memberof ExamService
   */
  public answerMode = false;

  /**
   * 考试界面需要的用户信息
   *
   * @type {UserInfo4Exam}
   * @memberof ExamService
   */
  public userInfo4Exam!: UserInfo4Exam;

  /**
   * 试卷信息
   *
   * @type {ExamPaperRespDto}
   * @memberof ExamService
   */
  public exam!: ExamPaperRespDto;

  /**
   * 参考答案信息
   *
   * @type {PaperAnswerRecord}
   * @memberof ExamService
   */
  public paperAnswerRecord!: PaperAnswerRecord;

  /**
   * 用户答案信息
   *
   * @type {UserAnswerRecord}
   * @memberof ExamService
   */
  public userAnswerRecord!: UserAnswerRecord;

  /**
   * 答卷信息
   * 只有查看答案模式下才会有这个信息
   *
   * @type {UserExam}
   * @memberof ExamService
   */
  public answerPaperInfo!: UserExam;

  // 是否开启了录制视频
  public introductionVideo = 0;
  // 是否开启了录制音频
  public introductionVideoConfig: any = null;

  /**
   * 视频录制按钮列表
   * UPLOAD_VIDEO(1,"上传讲解视频"),
   * RECORD_VIDEO(2,"录制讲解视频"),
   * UPDATE_VIDEO(3,"变更讲解视频"),
   * VIEW_VIDEO(4,"查看讲解视频")
   */
  public introductionVideoButtonList: number[] = [];

  /**
   * 评分记录
   * 只有查看答案模式下，且当前用户是管理员才能看到此记录
   *
   * @type {MarkRecordRespMap}
   * @memberof ExamService
   */
  public markRecordMap!: MarkRecordRespMap;

  public useTrtcFlag = false;

  // ai检测licence
  public licenceUrl = '';
  // 是否在不开启ai检测白名单内
  public enableDetect = false;

  constructor(public daoService: DaoService) {}

  /**
   * 当前后端确实返回了参考答案
   *
   * @readonly
   * @memberof ExamService
   */
  public get hasPaperAnswerRecord() {
    return (
      this.paperAnswerRecord && Object.keys(this.paperAnswerRecord).length > 0
    );
  }

  public setUserExamId(id: number) {
    this.userExamId = id;
  }
  public setAnswerPaperId(id: number) {
    this.answerPaperId = id;
  }
  public setAnswerMode(mode: boolean) {
    this.answerMode = mode;
  }
  public setReviewAreaId(id: number) {
    this.reviewAreaId = id;
  }

  /**
   * 1. 开始考试
   * 获取answerPaperId
   * 获取试卷信息
   *
   * 2. 查看试卷功能
   *
   * @return {*}
   * @memberof ExamService
   */
  public startExam(otherParams: any) {
    if (this.answerMode) {
      // 一次性获取所有信息
      return this.daoService
        .viewPaper(this.answerPaperId, this.reviewAreaId, otherParams)
        .then(res => {
          this.answerPaperInfo = res.answerPaperInfo || {};
          this.exam = res.examPaperInfo || {};
          this.userInfo4Exam = res.examUserInfo || {};
          this.userAnswerRecord = res.userAnswerMap || {};
          this.paperAnswerRecord = res.answerMap || {};

          // 默认选择第一道题
          this.questionService.selectDefault();
          // 初始化问题状态
          this.initQuestionStatus();
        });
    } else {
      // 分成两次获取信息

      return this.daoService.startExam(this.userExamId).then(res => {
        this.answerPaperId = res.answerPaperId;
        this.answerOrderLimit = res.answerOrderLimit;
        this.hasFinishedObjective = res.hasFinishedObjective;
        this.exam = res.examPaperResp;
        this.contestInfoId = res.contestInfoId as number;
        // 是否开启了三方直播监控
        this.useTrtcFlag = res.enableLiveMonitor;

        // 默认选择第一道题
        this.questionService.selectDefault();

        const remainTime = res.remainTime;
        // ai检测licence
        this.licenceUrl = res.licence || '';
        this.enableDetect = res.enableDetect || false;

        return Promise.all([
          // 获取用户信息
          this.getUserInfo4Exam(),
          // 获取用户答案
          this.getUserAnswer().then(() => {
            this.initQuestionStatus();
            this.envCheckService.setCheatService(this.cheatService);
            this.envCheckService.setAnswerPaperId(this.answerPaperId);
            // 使用三方直播监考功能，且剩余时间大于0
            if (this.useTrtcFlag && remainTime > 0) {
              // 这里新的使用trtc功能的要获取用户是否在三方视角的房间
              this.trtcService.getUserIsInRoom();
              this.trtcService.enterPcRoom();
            } else {
              // 这里就要做区分了 是否要使用老的检测入口
              this.envCheckService.startVideoMonitor();
            }
          }),
        ]);
      });
    }
  }

  public getMarkRecordFromServer(q: QuestionDto) {
    if (!q) return;
    this.markRecordMap = this.markRecordMap || {};
    if (this.markRecordMap[q.id]) return;
    if (IS_QUESTION_TYPE_PROGRAM(q.questionTypeId)) {
      this.daoService
        .viewMarkRecord(
          this.answerPaperId,
          q.id,
          this.exam.examId,
          this.reviewAreaId
        )
        .then(res => {
          this.markRecordMap[q.id] = res || {};
        });
    }
  }

  /**
   * 获取用户答案
   *
   * @return {*}
   * @memberof ExamService
   */
  public getUserAnswer() {
    return this.daoService.getUserAnswer(this.answerPaperId).then(res => {
      this.userAnswerRecord = res;
    });
  }

  /**
   * 获取单个题目的参考答案
   *
   * @param {number} id
   * @return {*}
   * @memberof ExamService
   */
  public getPaperAnswerByQuestionId(id: number) {
    return this.paperAnswerRecord && this.paperAnswerRecord[id];
  }

  /**
   * 获取某个题目的评分记录
   *
   * @param {number} id
   * @return {*}
   * @memberof ExamService
   */
  public getMarkRecordByQuestionId(id: number) {
    return this.markRecordMap && this.markRecordMap[id];
  }

  /**
   * 获取单个题目的用户答案
   *
   * @param {number} id
   * @return {*}
   * @memberof ExamService
   */
  public getUserAnswerByQuestionId(id: number) {
    return this.userAnswerRecord && this.userAnswerRecord[id];
  }

  /**
   * 更新本地的用户答案数据和状态
   *
   * @param {QuestionDto} questionDto
   * @param {UserAnswerDto} userAnswerDto
   * @memberof ExamService
   */
  public updateUserAnswerByQuestionId(
    questionDto: QuestionDto,
    userAnswerDto: UserAnswerDto
  ) {
    if (userAnswerDto) {
      this.userAnswerRecord[questionDto.id] = userAnswerDto;
      this.initQuestionDtoStatus(questionDto);
    }
  }

  /**
   * 初始化选择题的选中状态
   * 初始化问题的完成状态
   * 页面加载的时候只调用一次
   *
   * @memberof ExamService
   */
  public initQuestionStatus() {
    for (const item of this.exam.questionsDtoList) {
      for (const obj of item.questionDtoList) {
        this.initQuestionDtoStatus(obj);
      }
    }
  }

  /**
   * 初始化单个题目的用户答案状态
   *
   * @param {QuestionDto} questionDto
   * @memberof ExamService
   */
  public initQuestionDtoStatus(questionDto: QuestionDto) {
    const questionId = questionDto.id;
    const paperAnswerObj = this.getPaperAnswerByQuestionId(questionId);
    const userAnswerObj = this.getUserAnswerByQuestionId(questionId);
    if (userAnswerObj) {
      questionDto.questionStatus = 'completed';
      const answer = userAnswerObj.userAnswer;
      if (answer) {
        if (IS_QUESTION_TYPE_CHOICE(questionDto.questionTypeId)) {
          const answerArr = answer.split(',');
          const answerIndexArr = answerArr.map(letter =>
            letterToNumber(letter)
          );
          for (const i of answerIndexArr) {
            questionDto.options[i].checked = 'checked';
          }

          if (this.answerMode) {
            if (paperAnswerObj && paperAnswerObj.answerContent) {
              const paperAnswerArr = paperAnswerObj.answerContent
                .split(',')
                .map(letter => letterToNumber(letter));
              if (this.isCorrentAnswer(paperAnswerArr, answerIndexArr)) {
                questionDto.questionStatus = 'success';
              } else {
                questionDto.questionStatus = 'error';
              }

              answerIndexArr.forEach(index => {
                if (paperAnswerArr.includes(index)) {
                  questionDto.options[index].checked = 'checked-success';
                } else {
                  questionDto.options[index].checked = 'checked-error';
                }
              });
            }
          }
        } else if (IS_QUESTION_TYPE_FILL_BLANK(questionDto.questionTypeId)) {
          questionDto.userAnswer = answer;
          if (this.answerMode) {
            if (paperAnswerObj && paperAnswerObj.answerContent) {
              if (paperAnswerObj.answerContent.trim() === answer.trim()) {
                questionDto.questionStatus = 'success';
              } else {
                questionDto.questionStatus = 'error';
              }
            }
          }
        } else if (IS_QUESTION_TYPE_FILE(questionDto.questionTypeId)) {
          let answerList: any[] = [];
          try {
            answerList = JSON.parse(answer);
          } catch (e) {
            logger.error(e);
          }
          const commonAnswerRules = questionDto.commonAnswer?.answerRules || [];
          const uniqueKey = 'fileDescriptionKey';
          commonAnswerRules.forEach((item: any) => {
            const answerItem = answerList.find(
              (el: any) => el[uniqueKey] === item[uniqueKey]
            );
            if (answerItem) {
              item.userAnswer = answerItem.userAnswer;
            }
          });
        }
      }
    }
  }

  /**
   * 判断用户答案和参考答案是否一致
   *
   * @memberof ExamService
   */
  public isCorrentAnswer(paperAnswerArr: number[], userAnswerArr: number[]) {
    if (paperAnswerArr.length === userAnswerArr.length) {
      return userAnswerArr.every(ans => paperAnswerArr.includes(ans));
    }
    return false;
  }

  /**
   * 从ide返回时，需要重新同步改题目的作答状态
   *
   * @memberof ExamService
   */
  public syncQuestionStatus() {
    const questionDto = this.questionService.question;
    const questionId = questionDto.id;
    const userAnswer = this.getUserAnswerByQuestionId(questionId);
    if (!userAnswer) {
      return this.daoService
        .getQuestionAndAnswer(
          this.answerPaperId,
          this.exam.examId,
          this.questionService.question.id
        )
        .then(res => {
          this.updateUserAnswerByQuestionId(questionDto, res.userAnswerResp);
        });
    }
  }

  /**
   * 获取考试界面的用户信息
   *
   * @return {*}
   * @memberof ExamService
   */
  public getUserInfo4Exam() {
    return this.daoService.getUserInfo4Exam(this.userExamId).then(res => {
      this.userInfo4Exam = res;
    });
  }

  /**
   * 保存客观题答案
   *
   * @param {string} answer
   * @param {QuestionDto} question
   * @memberof ExamService
   */
  public saveUserAnswer(answer: string, question: QuestionDto) {
    question.questionStatus = 'completed';
    return this.daoService
      .postUserAnswer({
        answerPaperId: this.answerPaperId,
        questionId: question.id,
        questionPaperId: this.exam.examId,
        questionType: question.questionTypeId,
        userExamId: this.userExamId,
        userAnswer: answer,
      })
      .then(res => {
        this.updateUserAnswerByQuestionId(question, res);
      });
  }

  /**
   * 保存附件题答案
   * @param answerData
   * @param question
   * @returns
   */
  public saveUserAttachmentAnswer(
    answerData: AttachmentAnswerItem[],
    question: QuestionDto,
    title: string
  ) {
    return this.daoService
      .postUserAnswer({
        answerPaperId: this.answerPaperId,
        questionId: question.id,
        questionPaperId: this.exam.examId,
        questionType: question.questionTypeId,
        userExamId: this.userExamId,
        attachAnswerList: answerData,
        creationTitle: title,
      })
      .then(res => {
        question.questionStatus = 'completed';
        this.updateUserAnswerByQuestionId(question, res);
      });
  }

  /**
   * 保存自由创作题的标题和描述
   *
   * @param {string} title
   * @param {string} summary
   * @param {QuestionDto} question
   * @return {*}
   * @memberof ExamService
   */
  public saveCreationTitleAndSummary(
    title: string,
    summary: string,
    question: QuestionDto
  ) {
    question.questionStatus = 'completed';
    return this.daoService
      .postUserAnswer({
        answerPaperId: this.answerPaperId,
        questionId: question.id,
        questionPaperId: this.exam.examId,
        questionType: question.questionTypeId,
        userExamId: this.userExamId,
        creationTitle: title,
        creationSummary: summary,
      })
      .then(res => {
        this.updateUserAnswerByQuestionId(question, res);
      });
  }

  /**
   * 上报开始作答主观题操作接口
   * @returns
   */
  public applyFinishObjectiveRecord() {
    return this.daoService.postFinishObjectiveRecord({
      userExamId: this.userExamId,
      remainTime: this.countdownService.remainTime,
    });
  }

  /**
   * 提交整个试卷的接口
   *
   * @return {*}
   * @memberof ExamService
   */
  public submitExam() {
    return this.daoService.submitExam(this.userExamId).catch(e => e);
  }

  public destroy() {
    this.destroyed = true;
    if (this.useTrtcFlag) {
      // 退出trtc房间
      this.trtcService.exitRoom();
    } else {
      // 停止视频监控
      this.envCheckService.dispose();
    }
    // 停止防作弊
    this.cheatService.dispose();
    // 停止倒计时的回调 - 也就是停止自动交卷
    this.countdownService.dispose();
  }

  /**
   * 获取视频录制的规则
   * @returns
   */
  public getRecordButtonRule() {
    return this.daoService
      .getIntroductionBtnRules({
        userExamId: this.userExamId,
      })
      .then((res: any) => {
        this.introductionVideo = res.introductionVideo;
        this.introductionVideoConfig = res.videoConfig;
        this.introductionVideoButtonList = res.buttonList || [];
        return {
          introductionVideo: res.introductionVideo,
          introductionVideoConfig: res.videoConfig,
        };
      });
  }

  /**
   * 获取视频录制的url
   * @returns
   */
  public getRecordVideoUrl() {
    return this.daoService
      .getIntroductionVideo({
        userExamId: this.userExamId,
      })
      .then((res: any) => {
        return res;
      });
  }
  /**
   * 上传视频录制的url
   * @param url
   * @param optSource
   * @returns
   */
  public uploadRecordVideoData({
    url,
    optSource,
  }: {
    url: string;
    optSource: string;
  }) {
    return this.daoService
      .saveIntroductionVideo({
        userExamId: this.userExamId,
        optSource,
        videoUrl: url,
      })
      .then((res: any) => {
        return res;
      });
  }
}
